// src/tenants/registry.js

import { inmoceanTenantConfig } from './inmocean/config';
import { project1818TenantConfig } from './project1818/config';

/**
 * Per-tenant configuration registry.
 *
 * Currently has one tenant: InMocean. Future tenants register here by:
 *   1. Adding their config file at src/tenants/<tenant>/config.js
 *   2. Importing it here
 *   3. Adding a registry entry keyed by their canonical companyId
 *      (and any legacy ID forms — see InMocean's dual-key entry)
 *
 * Each tenant config can include a `permissions` field with `tenant`
 * (role overrides) and `groups` (group templates) — see PermissionService
 * + getTenantPermissions / getTenantGroupPermissions helpers below.
 *
 * Mirrors the backend per-tenant pattern at functions/tenantExtractions/<tenant>.js.
 *
 * NOTE: 'company_demo' exists in the codebase as a template/test surface
 * (functions/tenantExtractions/demo.js) but has no real users. It is
 * intentionally NOT registered here. When a real second tenant onboards,
 * that tenant gets its own config file + registry entry.
 *
 * See tenants/inmocean/CLAUDE.md and root CLAUDE.md §5.1 for the C
 * operating model context.
 */
export const tenantRegistry = {
    company_inmocean: inmoceanTenantConfig,                  // legacy ID — retained for backward compat
    [inmoceanTenantConfig.id]: inmoceanTenantConfig,         // canonical slug per ADR 0003
    [project1818TenantConfig.id]: project1818TenantConfig,
};

/**
 * Get the config for a given companyId. Returns undefined for unknown tenants.
 * Callers handle the undefined case (typically: fall through to the generic
 * unregistered-tenant chrome in TenantRouter).
 */
export function getTenantConfig(companyId) {
    return tenantRegistry[companyId];
}

/**
 * List all registered tenants with their canonical companyId attached.
 * Used by SystemSwitcher to render the tenant list.
 *
 * Deduplicates alias keys: emits one entry per unique config reference.
 * Multiple registry keys may point to the same config (e.g., legacy +
 * canonical tenant ID forms — see tenantRegistry comment). The emitted
 * `companyId` is always the canonical `config.id`, never the legacy key.
 */
export function listTenants() {
    const seen = new Set();
    const result = [];
    for (const [, config] of Object.entries(tenantRegistry)) {
        if (seen.has(config)) continue;
        seen.add(config);
        result.push({ companyId: config.id, ...config });
    }
    return result;
}

/**
 * Look up the tenant-specific role permission overrides for a given companyId.
 * Returns null if the tenant is not registered or has no permissions config.
 *
 * Used by PermissionService to compose final permissions: callers spread
 * basePermissions (DEFAULT_PERMISSIONS[role]) and these tenant overrides.
 */
export function getTenantPermissions(companyId) {
    return tenantRegistry[companyId]?.permissions?.tenant ?? null;
}

/**
 * Look up the tenant-specific group permission templates for a given companyId.
 * Returns null if the tenant is not registered or has no group permissions.
 */
export function getTenantGroupPermissions(companyId) {
    return tenantRegistry[companyId]?.permissions?.groups ?? null;
}
